import { useState, useCallback, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { SendButton } from "@/components/SendButton";
import { cn } from "@/lib/utils";

interface ChatInputProps {
  /** Called when the user submits a question. */
  onSubmit: (question: string) => void;
  /** Whether the input is disabled (e.g. during loading). */
  disabled?: boolean;
  /** Whether a query is currently being generated. */
  loading?: boolean;
}

/**
 * Auto-sizing textarea with a send button for submitting natural-language dataset questions.
 * Features container-level focus states, smooth send-button transitions, and a keyboard shortcut hint.
 */
export default function ChatInput({
  onSubmit,
  disabled,
  loading,
}: ChatInputProps) {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasContent = value.trim().length > 0;

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) {
      return;
    }
    onSubmit(trimmed);
    setValue("");
  }, [value, disabled, onSubmit]);

  const handleContainerClick = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <div className="relative">
      {/* biome-ignore lint: click-to-focus is an enhancement, keyboard users interact directly with the textarea */}
      <div
        className={cn(
          "flex items-end gap-2 rounded-xl border bg-background p-3 cursor-text transition-all duration-200",
          focused
            ? "border-foreground/15 shadow-md"
            : "border-border shadow-sm hover:border-foreground/10",
          disabled && "pointer-events-none opacity-60",
        )}
        onClick={handleContainerClick}
      >
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onSend={handleSubmit}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Ask about your data..."
          disabled={disabled}
          className="flex-1 min-h-0 border-0 shadow-none px-1 py-1.5 leading-relaxed placeholder:text-muted-foreground/60 focus-visible:ring-0"
        />

        <div className="flex items-center gap-1.5 shrink-0">
          <SendButton
            onClick={handleSubmit}
            disabled={disabled || !hasContent}
            loading={loading}
            className={cn(
              "h-8 w-8 rounded-lg transition-all duration-200",
              hasContent && !disabled
                ? "scale-100 opacity-100"
                : "scale-95 opacity-40",
            )}
          />
        </div>
      </div>

      {/* Loading progress bar */}
      {loading && (
        <div className="absolute bottom-0 left-3 right-3 h-0.5 overflow-hidden rounded-full">
          <div className="h-full w-1/3 animate-[shimmer_1.5s_ease-in-out_infinite] rounded-full bg-primary/40" />
        </div>
      )}
    </div>
  );
}
